import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Clock, GitBranch } from 'lucide-react';
import { blogPosts } from './Blog';

const articleContent: Record<string, React.ReactNode> = {
  'react-fiber-explained': (
    <>
      <p>Every time you call <code>setState</code>, something intricate happens inside React. Before Fiber (React 16), that "something" was a recursive, synchronous tree walk that could block the main thread for tens of milliseconds. Fiber changed everything.</p>

      <h2>What Is Fiber?</h2>
      <p>Fiber is React's internal reconciliation engine — rewritten from scratch in React 16. The key insight: instead of recursion, Fiber uses a <strong>linked list of work units</strong>. Each unit can be paused, resumed, or thrown away.</p>

      <pre><code>{`// Simplified Fiber node structure
interface FiberNode {
  type: string | Function;
  key: string | null;
  child: FiberNode | null;      // first child
  sibling: FiberNode | null;    // next sibling
  return: FiberNode | null;     // parent
  pendingProps: Props;
  memoizedProps: Props;
  memoizedState: any;
  effectTag: EffectTag;
  alternate: FiberNode | null;  // the "double buffer"
}`}</code></pre>

      <h2>The Work Loop</h2>
      <p>React's scheduler runs a work loop that processes one fiber at a time. Between each unit, it checks: "Is there higher-priority work? Has the user interacted?" If yes — pause.</p>

      <pre><code>{`function workLoop(deadline: IdleDeadline) {
  let shouldYield = false;
  
  while (nextUnitOfWork && !shouldYield) {
    nextUnitOfWork = performUnitOfWork(nextUnitOfWork);
    // Check if we've run out of time this frame
    shouldYield = deadline.timeRemaining() < 1;
  }
  
  if (!nextUnitOfWork && wipRoot) {
    // All work done — commit to DOM
    commitRoot();
  }
  
  requestIdleCallback(workLoop);
}`}</code></pre>

      <h2>Phases: Render vs Commit</h2>
      <p>Fiber splits work into two phases:</p>
      <ul>
        <li><strong>Render phase</strong> — interruptible. React builds the work-in-progress tree, diffs against current, marks effects. No DOM mutations happen here.</li>
        <li><strong>Commit phase</strong> — synchronous and uninterruptible. React applies all the mutations, runs effects (<code>useLayoutEffect</code>, then <code>useEffect</code>).</li>
      </ul>

      <h2>Double Buffering</h2>
      <p>React maintains two trees simultaneously: <code>current</code> (what's on screen) and <code>workInProgress</code> (what it's building). Each fiber has an <code>alternate</code> pointer to its counterpart. When the commit phase finishes, React swaps the pointers — the <code>workInProgress</code> becomes <code>current</code>.</p>

      <h2>What This Means for You</h2>
      <p>Because the render phase is interruptible, your render functions can run <strong>multiple times</strong> for a single update. This is why React 18's Strict Mode double-invokes renders in development — to surface bugs from impure render functions.</p>

      <pre><code>{`// ❌ Impure — breaks with concurrent rendering
let count = 0;
function Counter() {
  count++; // side effect in render!
  return <div>{count}</div>;
}

// ✅ Pure — safe
function Counter() {
  const [count, setCount] = useState(0);
  return <div>{count}</div>;
}`}</code></pre>

      <h2>Practical Takeaways</h2>
      <ul>
        <li>Keep render functions pure — no side effects, no mutations</li>
        <li>Use <code>useTransition</code> to mark non-urgent updates — Fiber can deprioritize them</li>
        <li>Understand that <code>useLayoutEffect</code> runs synchronously in the commit phase — use sparingly</li>
        <li>The Profiler API lets you measure render cost per fiber — use it before optimizing</li>
      </ul>

      <p>Understanding Fiber makes you a better React developer not because you'll ever touch it directly, but because every performance decision makes more sense once you see the engine underneath.</p>
    </>
  ),
  'typescript-discriminated-unions': (
    <>
      <p>Here's a pattern I see over and over in TypeScript codebases — using a boolean flag to represent state that's actually a union of distinct cases:</p>

      <pre><code>{`// ❌ The boolean flag antipattern
interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: Error | null;
}
// Now you have impossible states:
// { loading: true, data: {...}, error: Error } 🤯`}</code></pre>

      <h2>Enter Discriminated Unions</h2>
      <p>A discriminated union is a union of types that share a common literal property — the "discriminant". TypeScript uses it to narrow the type in branches.</p>

      <pre><code>{`type AsyncState<T> =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'success'; data: T }
  | { status: 'error'; error: Error };

function render<T>(state: AsyncState<T>) {
  switch (state.status) {
    case 'idle':    return <Placeholder />;
    case 'loading': return <Spinner />;
    case 'success': return <Data data={state.data} />; // ✅ data is T here
    case 'error':   return <Error msg={state.error.message} />;
  }
}`}</code></pre>

      <h2>Real-World: API Response Typing</h2>
      <p>I used this extensively at Syndr for typing WebSocket messages from the exchange:</p>

      <pre><code>{`type ExchangeMessage =
  | { type: 'order_placed';  orderId: string; price: number }
  | { type: 'order_filled';  orderId: string; fillPrice: number }
  | { type: 'order_cancelled'; orderId: string; reason: string }
  | { type: 'position_update'; position: Position }
  | { type: 'error'; code: number; message: string };

ws.onmessage = (event) => {
  const msg: ExchangeMessage = JSON.parse(event.data);
  
  if (msg.type === 'error') {
    // TypeScript knows msg.code and msg.message exist
    reportError(msg.code, msg.message);
  }
};`}</code></pre>

      <h2>Exhaustiveness Checking</h2>
      <p>The killer feature: TypeScript can verify you handled every case with the <code>never</code> trick.</p>

      <pre><code>{`function assertNever(x: never): never {
  throw new Error('Unhandled case: ' + JSON.stringify(x));
}

function handleMessage(msg: ExchangeMessage) {
  switch (msg.type) {
    case 'order_placed':   return handlePlaced(msg);
    case 'order_filled':   return handleFilled(msg);
    case 'order_cancelled': return handleCancelled(msg);
    case 'position_update': return handlePosition(msg);
    case 'error':           return handleError(msg);
    default: return assertNever(msg); // compile error if you miss a case
  }
}`}</code></pre>

      <h2>With Zod for Runtime Safety</h2>

      <pre><code>{`import { z } from 'zod';

const ExchangeMessageSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('order_placed'), orderId: z.string(), price: z.number() }),
  z.object({ type: z.literal('error'), code: z.number(), message: z.string() }),
  // ...
]);

// Validates at runtime AND gives you the TypeScript type
type ExchangeMessage = z.infer<typeof ExchangeMessageSchema>;`}</code></pre>

      <p>Stop using boolean flags. Discriminated unions make impossible states impossible — your future self will thank you.</p>
    </>
  ),
};

const defaultContent = (post: typeof blogPosts[0]) => (
  <>
    <p>{post.excerpt}</p>
    <p style={{ color: 'var(--text2)', fontStyle: 'italic', marginTop: 32, padding: '24px', background: 'var(--surface)', borderRadius: 12, borderLeft: `3px solid ${post.color}` }}>
      📝 Full article coming soon. I'm actively writing this as part of my preparation journey — follow along on GitHub or check back in a few days!
    </p>
    <h2>What This Article Will Cover</h2>
    <ul>
      <li>Core concepts from first principles</li>
      <li>Real code examples from production codebases</li>
      <li>Common mistakes and how to avoid them</li>
      <li>Performance implications and trade-offs</li>
      <li>Interview-ready mental models</li>
    </ul>
  </>
);

export default function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const post = blogPosts.find(p => p.slug === slug);

  if (!post) {
    return (
      <div style={{ paddingTop: 120, textAlign: 'center', padding: '120px 24px' }}>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 48, marginBottom: 16 }}>404</h1>
        <p style={{ color: 'var(--text2)', marginBottom: 32 }}>Post not found.</p>
        <Link to="/blog" style={{ color: 'var(--cyan)' }}>← Back to Blog</Link>
      </div>
    );
  }

  const content = articleContent[post.slug] || defaultContent(post);

  return (
    <div style={{ paddingTop: 64, minHeight: '100vh' }}>
      {/* Back */}
      <div style={{ padding: '32px 24px 0', maxWidth: 780, margin: '0 auto' }}>
        <Link to="/blog" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--text2)', fontSize: 13, marginBottom: 40 }}>
          <ArrowLeft size={14} /> Back to Blog
        </Link>
      </div>

      {/* Article Header */}
      <header style={{ padding: '0 24px 40px', maxWidth: 780, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: post.color, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{post.category}</span>
          <span style={{ color: 'var(--text3)' }}>·</span>
          <span style={{ color: 'var(--text3)', fontSize: 13, display: 'flex', alignItems: 'center', gap: 4 }}><Clock size={12} /> {post.readTime} read</span>
          <span style={{ color: 'var(--text3)' }}>·</span>
          <span style={{ color: 'var(--text3)', fontSize: 13, fontFamily: 'var(--font-mono)' }}>{post.date}</span>
        </div>
        <h1 style={{
          fontFamily: 'var(--font-display)', fontSize: 'clamp(28px, 4vw, 48px)',
          fontWeight: 800, lineHeight: 1.1, letterSpacing: '-0.025em', marginBottom: 20,
        }}>{post.title}</h1>
        <p style={{ color: 'var(--text2)', fontSize: 18, lineHeight: 1.7, marginBottom: 24 }}>{post.excerpt}</p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', paddingBottom: 32, borderBottom: '1px solid var(--border)' }}>
          {post.tags.map(t => <span key={t} className="tag">{t}</span>)}
        </div>
      </header>

      {/* Article Body */}
      <article style={{ padding: '0 24px 80px', maxWidth: 780, margin: '0 auto' }}>
        <style>{`
          article p { color: var(--text); font-size: 16px; line-height: 1.85; margin-bottom: 20px; }
          article h2 { font-family: var(--font-display); font-size: 26px; font-weight: 700; letter-spacing: -0.02em; margin: 40px 0 16px; color: var(--text); }
          article h3 { font-family: var(--font-display); font-size: 20px; font-weight: 600; margin: 28px 0 12px; color: var(--text); }
          article ul, article ol { color: var(--text); font-size: 16px; line-height: 1.85; padding-left: 24px; margin-bottom: 20px; }
          article li { margin-bottom: 8px; }
          article strong { color: var(--text); font-weight: 600; }
          article code { font-family: var(--font-mono); font-size: 13px; background: var(--surface2); color: var(--cyan); padding: 2px 7px; border-radius: 5px; }
          article pre { background: var(--bg3); border: 1px solid var(--border); border-radius: 12px; padding: 24px; overflow-x: auto; margin: 24px 0; }
          article pre code { background: none; color: var(--text2); padding: 0; font-size: 13px; line-height: 1.7; }
        `}</style>
        {content}

        {/* Author Card */}
        <div style={{
          marginTop: 64, padding: 28, background: 'var(--surface)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius)', display: 'flex', gap: 20, alignItems: 'flex-start',
        }}>
          <div style={{
            width: 52, height: 52, borderRadius: 14, flexShrink: 0,
            background: 'linear-gradient(135deg, var(--cyan), var(--violet))',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 20, color: '#080b10',
          }}>P</div>
          <div>
            <p style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 16, marginBottom: 4 }}>Parmeshwar</p>
            <p style={{ color: 'var(--text2)', fontSize: 14, lineHeight: 1.6 }}>
              Senior Full Stack Developer · IIT Indore · Building at the intersection of React, AI & Web3.
            </p>
            <div style={{ display: 'flex', gap: 12, marginTop: 12 }}>
              <a href="https://github.com/PARMESHWARPANWAR" target="_blank" rel="noreferrer" style={{ color: 'var(--text3)', display: 'flex', alignItems: 'center', gap: 5, fontSize: 13 }}>
                <GitBranch size={14} /> GitHub
              </a>
            </div>
          </div>
        </div>

        {/* More Posts */}
        <div style={{ marginTop: 64 }}>
          <p style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text3)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 24 }}>More Articles</p>
          <div style={{ display: 'grid', gap: 12 }}>
            {blogPosts.filter(p => p.slug !== slug).slice(0, 3).map(p => (
              <Link to={`/blog/${p.slug}`} key={p.slug} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16,
                padding: '16px 20px', background: 'var(--surface)', border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)', transition: 'border 0.2s',
              }}
              onMouseOver={e => (e.currentTarget as HTMLElement).style.borderColor = 'var(--border2)'}
              onMouseOut={e => (e.currentTarget as HTMLElement).style.borderColor = 'var(--border)'}>
                <div>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: p.color, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{p.category} · {p.readTime}</span>
                  <p style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 15, marginTop: 4 }}>{p.title}</p>
                </div>
                <ArrowLeft size={14} style={{ transform: 'rotate(180deg)', color: 'var(--text3)', flexShrink: 0 }} />
              </Link>
            ))}
          </div>
        </div>
      </article>
    </div>
  );
}
